/**
 * Code lists shared by backend and frontend (ADR-0006 numbering, ADR-0009 roles).
 * Adding a code: update this file AND the CHECK constraints in
 * backend/src/db/schema.sql, then record it in an ADR (types: ADR-0006, ADR-0012).
 */

export const COMPANY_CODES = ["ITA", "BL", "CC", "GDM", "CPY"] as const;
export type CompanyCode = (typeof COMPANY_CODES)[number];

/**
 * Code lists (ADR-0016): the database (code_values) is the source of truth and
 * admins manage it. These are the defaults the app starts with; the frontend
 * replaces them in place with applyCodeLists() after loading /code-lists, and
 * the API validates against the database, never against these arrays.
 */
export interface CodeValue {
  kind: "type" | "area" | "classification" | "retention";
  code: string;
  label: string;
  short_label: string | null;
  color: string | null;
  sort: number;
  retired: boolean;
  locked: boolean;
  /** True when any document uses it (the code can then never change or go). */
  used?: boolean;
}
export type TypeCode = string;
export type AreaCode = string;

/** Active type codes, in display order. */
export const TYPE_CODES: string[] = ["POL", "SOP", "HOW", "STD", "PRO", "GUI", "PLN", "FRM", "TMP", "CHK", "RPT", "ASM", "AUD", "REG", "REC", "AGR", "CON", "MAN", "KB", "DIA", "CFG", "ARCH", "SPEC"];
/** Short names for chips and badges. */
export const TYPE_LABELS: Record<string, string> = {
  POL: "Policy", SOP: "SOP", HOW: "How-To", STD: "Standard", PRO: "Process", GUI: "Guideline", PLN: "Plan", FRM: "Form",
  TMP: "Template", CHK: "Checklist", RPT: "Report", ASM: "Assessment", AUD: "Audit", REG: "Register", REC: "Record",
  AGR: "Agreement", CON: "Contract", MAN: "Manual", KB: "KB Article", DIA: "Diagram", CFG: "Configuration",
  ARCH: "Architecture", SPEC: "Specification", PRC: "Procedure", PRS: "Process", GDL: "Guideline", TPL: "Template",
};
/** Full names (forms, details). */
export const TYPE_NAMES: Record<string, string> = { ...TYPE_LABELS, SOP: "Standard Operating Procedure", HOW: "How-To Guide", KB: "Knowledge Base Article", ARCH: "Architecture Document" };
/** Document-type icon colors (ADR-0011, ADR-0012). Data colors, not decoration. */
export const TYPE_COLORS: Record<string, string> = {
  POL: "#3B82C4", SOP: "#DC6D27", HOW: "#D9486B", STD: "#4B5BA8", PRO: "#7C5CC4", GUI: "#6E8B2E", PLN: "#0F8BB5", FRM: "#2F9E5B",
  TMP: "#A07A12", CHK: "#1F9E8F", RPT: "#5D6470", ASM: "#B4561C", AUD: "#8A3FA0", REG: "#2E7D6B", REC: "#6B7A8F", AGR: "#9C4A2E",
  CON: "#7A5230", MAN: "#3E6FA8", KB: "#C2408A", DIA: "#0E8C9E", CFG: "#59708A", ARCH: "#4F6D2A", SPEC: "#6A5ACD",
  PRC: "#1F9E8F", PRS: "#7C5CC4", GDL: "#6E8B2E", TPL: "#A07A12",
};

export const AREA_CODES: string[] = ["SEC", "IT", "SD", "ENG", "OPS", "HR", "FIN", "SAL", "MKT", "LEG", "EXE", "CMP", "PM", "QA", "BCM", "VEN", "ADM"];
export const AREA_LABELS: Record<string, string> = {
  SEC: "Information Security", IT: "Information Technology", SD: "Software Development", ENG: "Engineering", OPS: "Operations",
  HR: "Human Resources", FIN: "Finance", SAL: "Sales", MKT: "Marketing", LEG: "Legal", EXE: "Executive & Corporate Governance",
  CMP: "Compliance & Risk Management", PM: "Project Management", QA: "Quality Assurance", BCM: "Business Continuity & Disaster Recovery",
  VEN: "Vendor Management", ADM: "Administration & Facilities",
};

/** Classification (ADR-0016). REST limits readers; the others are labels. */
export const RESTRICTED = "REST";
export const DEFAULT_CLASSIFICATION = "INT";
export const CLASSIFICATION_CODES: string[] = ["PBL", "INT", "CONF", "REST"];
export const CLASSIFICATION_LABELS: Record<string, string> = { PBL: "Public", INT: "Internal", CONF: "Confidential", REST: "Restricted" };
export const RETENTION_CODES: string[] = ["CUR3", "Y7", "PERM"];
export const RETENTION_LABELS: Record<string, string> = { CUR3: "While current, then 3 years", Y7: "7 years after it is retired", PERM: "Permanent" };

/** Visibility (ADR-0016). Client Shared and Public take effect with client access (Phase 3). */
export const VISIBILITIES = ["internal", "client", "public"] as const;
export type Visibility = (typeof VISIBILITIES)[number];
export const VISIBILITY_LABELS: Record<Visibility, string> = { internal: "Internal only", client: "Client shared", public: "Public" };

/** Status codes are fixed (DocVault behavior), shown as words, and reserved: no list may reuse them. */
export const STATUS_CODES: Record<string, string> = {
  DFT: "Draft", REV: "In review", APR: "Approved", PUB: "Published", SUP: "Superseded", ARC: "Archived", RET: "Retired",
};
/** Codes go into permanent document numbers: 2-5 capitals/digits, starting with a letter. */
export const CODE_RE = /^[A-Z][A-Z0-9]{1,4}$/;
export const RETENTION_CODE_RE = /^[A-Z][A-Z0-9]{1,7}$/;

/** Replace the defaults with the lists from the server (frontend, after /code-lists). */
export function applyCodeLists(list: CodeValue[]) {
  const sorted = [...list].sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code));
  const fill = (arr: string[], kind: CodeValue["kind"]) => {
    arr.length = 0;
    for (const v of sorted) if (v.kind === kind && !v.retired) arr.push(v.code);
  };
  fill(TYPE_CODES, "type"); fill(AREA_CODES, "area"); fill(CLASSIFICATION_CODES, "classification"); fill(RETENTION_CODES, "retention");
  for (const v of sorted) {
    if (v.kind === "type") {
      TYPE_LABELS[v.code] = v.short_label || v.label;
      TYPE_NAMES[v.code] = v.label;
      if (v.color) TYPE_COLORS[v.code] = v.color;
    } else if (v.kind === "area") AREA_LABELS[v.code] = v.label;
    else if (v.kind === "classification") CLASSIFICATION_LABELS[v.code] = v.label;
    else RETENTION_LABELS[v.code] = v.label;
  }
}

// ---- Video (ADR-0012): a file format allowed for How-Tos and SOPs ----------
export const VIDEO_TYPE_CODES: readonly string[] = ["HOW", "SOP"];
export const VIDEO_EXTENSIONS = ["mp4", "m4v", "mov", "webm"] as const;
export const MAX_FILE_BYTES = 100 * 1024 * 1024;   // documents
export const MAX_VIDEO_BYTES = 1024 * 1024 * 1024; // 1 GB
export const isVideoFile = (mime: string | null | undefined, name: string) =>
  /^video\//i.test(mime ?? "") || new RegExp(`\\.(${VIDEO_EXTENSIONS.join("|")})$`, "i").test(name);

// ---- Framework tags (ADR-0012): labels, not control mapping ---------------
export const FRAMEWORKS = ["CMMC", "NIST 800-171", "SOC 2", "HIPAA", "PCI DSS", "ISO 27001", "NIST CSF", "FTC Safeguards"] as const;
export const MAX_FRAMEWORK_TAGS = 12;
/** Tidy a typed tag; null if it isn't usable. Known tags keep their spelling. */
export function normalizeFramework(raw: string): string | null {
  const t = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (!t || t.length > 30 || !/^[\p{L}\p{N} .&/+-]+$/u.test(t)) return null;
  const known = FRAMEWORKS.find((f) => f.toLowerCase() === t.toLowerCase());
  return known ?? t;
}
export function normalizeFrameworks(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const x of list) {
    const n = normalizeFramework(String(x));
    if (n && !out.some((o) => o.toLowerCase() === n.toLowerCase())) out.push(n);
  }
  return out.slice(0, MAX_FRAMEWORK_TAGS);
}

// "reviewer" (ADR-0019); "clienthub_viewer" / "clienthub_editor" (ADR-0022, only where TechHub is on): checks a document before approval where a company rule asks for it.
// "clienthub_accounting" (2026-10-23, Bill): sees and changes agreement dollar values and opens signed PDFs, without admin rights.
// "reviewhub_viewer" / "reviewhub_editor" (ADR-0023, only where ReviewHub is on). Company admins are ReviewHub admins.
export const ROLES = ["reader", "author", "reviewer", "approver", "admin", "clienthub_viewer", "clienthub_editor", "clienthub_accounting", "reviewhub_viewer", "reviewhub_editor", "vendorhub_viewer", "vendorhub_editor"] as const;
/** Add-ons (ADR-0018/0022): switched on per company by a platform admin. */
export const MODULES = ["clienthub", "reviewhub", "vendorhub"] as const;
export type ModuleKey = (typeof MODULES)[number];
export type Role = (typeof ROLES)[number];

export const ACRONYM_CATEGORIES = ["Security", "Identity", "Network", "Cloud", "Operations", "Compliance", "Business"] as const;

/** Version states (document_versions.state). 'scheduled' = approved, effective later (ADR-0016). */
export type VersionState = "draft" | "in_review" | "changes_requested" | "scheduled" | "approved" | "discarded";
/** Document status (documents.status). */
export type DocStatus = "draft" | "published" | "archived" | "retired";

export const pad3 = (n: number) => String(n).padStart(3, "0");
/** The permanent document ID (ADR-0006, ADR-0016): never carries the version. */
export const baseNumber = (company: string, type: string, area: string, seq: number) =>
  `${company}-${type}-${area}-${pad3(seq)}`;

// ---- Versions (ADR-0016): MAJOR.MINOR.REVISION -------------------------------
export type Bump = "major" | "minor" | "revision";
export const BUMPS: Bump[] = ["major", "minor", "revision"];
export const BUMP_LABELS: Record<Bump, string> = {
  major: "Major - significant change or rewrite",
  minor: "Minor - updated steps or requirements",
  revision: "Revision - typo, formatting or clarification",
};
export type Semver = [number, number, number];
export const fmtVersion = (v: Semver | null | undefined) => (v ? `${v[0]}.${v[1]}.${v[2]}` : null);
/** The version a bump produces. The first approved release is always 1.0.0. */
export function nextVersion(prev: Semver | null | undefined, bump: Bump | null | undefined): Semver {
  if (!prev) return [1, 0, 0];
  const [M, m, p] = prev;
  if (bump === "major") return [M + 1, 0, 0];
  if (bump === "revision") return [M, m, p + 1];
  return [M, m + 1, 0];
}
/** "ITA-POL-SEC-001 v1.3.14" for display; the ID alone when there is no version yet. */
export const docLabel = (base: string, version: string | null | undefined) => (version ? `${base} v${version}` : base);
